import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiUpload, FiDownload, FiFileText, FiAlertCircle, FiCheckCircle } = FiIcons;

const TemplateImportModal = ({ isOpen, onClose, onImport }) => {
  const [dragActive, setDragActive] = useState(false);
  const [importedTemplate, setImportedTemplate] = useState(null);
  const [importError, setImportError] = useState('');

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    if (file.type !== 'application/json') {
      setImportError('Please select a valid JSON template file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const templateData = JSON.parse(e.target.result);
        
        // Validate template structure
        if (!templateData.name || !Array.isArray(templateData.elements)) {
          throw new Error('Invalid template format');
        }

        setImportedTemplate({
          ...templateData,
          id: Date.now().toString(),
          importedAt: new Date().toISOString()
        });
        setImportError('');
      } catch (error) {
        setImportError('Error parsing template file. Please check the file format.');
        setImportedTemplate(null);
      }
    };
    reader.readAsText(file);
  };

  const confirmImport = () => {
    if (importedTemplate) {
      onImport(importedTemplate);
      handleClose();
    }
  };

  const handleClose = () => {
    setImportedTemplate(null);
    setImportError('');
    setDragActive(false);
    onClose();
  };

  const downloadSampleTemplate = () => {
    const sampleTemplate = {
      name: "Sample Exam Template",
      elements: [
        {
          type: "header",
          content: "University Examination - Course Code",
          fontSize: 18,
          fontFamily: "Arial",
          align: "center"
        },
        {
          type: "image",
          src: "",
          width: 100,
          height: 80
        },
        {
          type: "text",
          content: "Instructions: Read all questions carefully before answering.",
          fontSize: 14,
          fontFamily: "Arial",
          align: "left"
        },
        {
          type: "line",
          thickness: 1,
          style: "solid"
        },
        {
          type: "text",
          content: "Section A: Multiple Choice Questions",
          fontSize: 16,
          fontFamily: "Arial",
          align: "left"
        },
        {
          type: "footer",
          content: "Page {pageNumber} of {totalPages}",
          fontSize: 10,
          fontFamily: "Arial",
          align: "center"
        }
      ],
      settings: {
        pageSize: "A4",
        margin: 20,
        orientation: "portrait"
      },
      createdAt: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(sampleTemplate, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_exam_template.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Import Template</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Import a previously exported template file
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Sample Template Download */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-blue-800 mb-1">Download Sample Template</h4>
                    <p className="text-sm text-blue-600">
                      Get a sample template to understand the file format
                    </p>
                  </div>
                  <button
                    onClick={downloadSampleTemplate}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <SafeIcon icon={FiDownload} />
                    <span>Download Sample</span>
                  </button>
                </div>
              </div>

              {/* File Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <SafeIcon icon={FiUpload} className="mx-auto text-4xl text-gray-400 mb-4" />
                <h4 className="text-lg font-medium text-gray-800 mb-2">Select Template File</h4>
                <p className="text-gray-600 mb-4">
                  Drag and drop your JSON template file here, or click to browse
                </p>
                <label className="inline-block">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileInput}
                    className="hidden"
                  />
                  <span className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
                    Choose JSON File
                  </span>
                </label>
                <p className="text-sm text-gray-500 mt-3">
                  Only JSON template files are supported
                </p>
              </div>

              {/* Import Error */}
              {importError && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 rounded-lg p-4"
                >
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiAlertCircle} className="text-red-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-red-800">Import Error</h4>
                      <p className="text-sm text-red-700">{importError}</p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Template Preview */}
              {importedTemplate && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-50 border border-green-200 rounded-lg p-4"
                >
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-green-800">Template Ready for Import</h4>
                      <div className="mt-2 space-y-1 text-sm text-green-700">
                        <p><strong>Name:</strong> {importedTemplate.name}</p>
                        <p><strong>Elements:</strong> {importedTemplate.elements?.length || 0}</p>
                        <p><strong>Page Size:</strong> {importedTemplate.settings?.pageSize || 'A4'}</p>
                        <p><strong>Orientation:</strong> {importedTemplate.settings?.orientation || 'Portrait'}</p>
                      </div>

                      {importedTemplate.elements && importedTemplate.elements.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-green-800 mb-2">Elements Preview:</p>
                          <div className="grid grid-cols-2 gap-2">
                            {importedTemplate.elements.map((element, index) => (
                              <div key={index} className="flex items-center space-x-2 text-xs bg-white rounded px-2 py-1">
                                <SafeIcon icon={FiFileText} className="text-green-600" />
                                <span className="capitalize">{element.type}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* File Format Help */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-2">Template File Format</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Template files must be in JSON format and include:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li><code>name</code>: Template name</li>
                    <li><code>elements</code>: Array of design elements</li>
                    <li><code>settings</code>: Page settings (size, margins, orientation)</li>
                  </ul>
                  <p className="mt-2">
                    Export any template from the editor to see the correct format.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <motion.button
                onClick={confirmImport}
                disabled={!importedTemplate}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                whileHover={{ scale: !importedTemplate ? 1 : 1.02 }}
                whileTap={{ scale: !importedTemplate ? 1 : 0.98 }}
              >
                <SafeIcon icon={FiUpload} />
                <span>Import Template</span>
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TemplateImportModal;